import React, { useEffect } from 'react';
import { useUser } from '../contexts/UserContext';

interface GoogleLoginProps {
  onError: (error: string) => void;
}

// Google Sign-In types (global)
interface GoogleConfig {
  client_id: string;
  callback: (response: { credential: string }) => void;
}

interface GoogleButtonConfig {
  theme: string;
  size: string;
  width: number;
  text: string;
  shape: string;
}

declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: GoogleConfig) => void;
          renderButton: (element: HTMLElement, config: GoogleButtonConfig) => void;
        };
      };
    };
  }
}

const GoogleLogin: React.FC<GoogleLoginProps> = ({ onError }) => {
  const { login } = useUser();

  useEffect(() => {
    const handleCredentialResponse = async (response: { credential: string }) => {
      try {
        const result = await fetch('/api/auth', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: response.credential,
          }),
        });

        const data = await result.json();

        if (data.success) {
          login(data.user);
        } else {
          onError('Authentication failed');
        }
      } catch (error) {
        console.error('Login error:', error);
        onError('Login failed. Please try again.');
      }
    };

    // Load Google Sign-In script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      if (!clientId) {
        console.error('Google Client ID not found in environment variables');
        onError('Google authentication is not configured');
        return;
      }
      
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCredentialResponse,
      });

      const buttonElement = document.getElementById('google-signin-button');
      if (buttonElement) {
        window.google.accounts.id.renderButton(buttonElement, {
          theme: 'outline',
          size: 'large',
          width: 300,
          text: 'signin_with',
          shape: 'rectangular',
        });
      }
    };

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [login, onError]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background">
      <div className="bg-card rounded-2xl shadow-xl p-8 text-center border border-border">
        <h1 className="text-3xl font-bold text-card-foreground mb-2">Welcome to Giorgie</h1>
        <p className="text-muted-foreground mb-8">Your AI-powered travel concierge</p>
        
        <div id="google-signin-button" className="mb-4"></div>
        
        <p className="text-sm text-muted-foreground">
          Sign in to start planning your perfect trip
        </p>
      </div>
    </div>
  );
};

export default GoogleLogin;